
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface PendingTripHandlerProps {
  user: User | null;
}

/**
 * Component to handle pending trip joins after authentication
 */
const PendingTripHandler: React.FC<PendingTripHandlerProps> = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handlePendingTripJoin = async () => {
      if (!user) return;
      
      const pendingTripCode = sessionStorage.getItem('pendingTripCode');
      const pendingTripId = sessionStorage.getItem('pendingTripId');
      const pendingTripName = sessionStorage.getItem('pendingTripName');
      
      if (pendingTripCode && pendingTripId) {
        try {
          console.log("Attempting to join pending trip:", pendingTripId, "for user:", user.id);
          
          // Check if user is already a member of this trip
          const { data: existingMembership, error: membershipCheckError } = await supabase
            .from('trip_members')
            .select('id')
            .eq('trip_id', pendingTripId)
            .eq('user_id', user.id)
            .maybeSingle();
            
          if (membershipCheckError) {
            console.error("Error checking membership:", membershipCheckError);
          }
          
          // If not already a member, add to trip_members
          if (!existingMembership) {
            console.log("User is not already a member, adding to trip");
            const { error: memberError } = await supabase
              .from('trip_members')
              .insert({
                trip_id: pendingTripId,
                user_id: user.id
              });

            if (memberError) {
              console.error("Error joining trip:", memberError);
              toast({
                title: "Error joining trip",
                description: memberError.message,
                variant: "destructive"
              });
              return;
            }
          } else {
            console.log("User is already a member of this trip");
          }

          const tripName = pendingTripName || 'the trip';
          toast({
            title: "Success!",
            description: `You've successfully joined ${tripName}!`,
          });
          
          // Clear stored trip data
          sessionStorage.removeItem('pendingTripCode');
          sessionStorage.removeItem('pendingTripId');
          sessionStorage.removeItem('pendingTripName');
          
          // Navigate to trip page
          navigate(`/trips/${pendingTripId}/itinerary`);
        } catch (error) {
          console.error("Error handling pending trip join:", error);
          toast({
            title: "Failed to join trip",
            description: "There was an error joining the trip. Please try again.",
            variant: "destructive"
          });
        }
      }
    };

    handlePendingTripJoin();
  }, [user, navigate, toast]);

  return null;
};

export default PendingTripHandler;
